import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Check, Download, Video, Image as ImageIcon, Hash, Share2, Facebook } from "lucide-react";

const TYPES = [
  { key: "artist", label: "Artiste", emoji: "🎤" },
  { key: "track",  label: "Morceau / Clip", emoji: "🎵" },
  { key: "short",  label: "Short", emoji: "📱" },
  { key: "ad",     label: "Annonce", emoji: "🛒" },
  { key: "upload", label: "Vidéo (upload)", emoji: "📤" },
  { key: "external", label: "YouTube / TikTok", emoji: "🌐" },
  { key: "custom", label: "Texte libre", emoji: "✍️" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export const AdminViralStudioPanel = () => {
  const [sources, setSources] = useState({ artists: [], tracks: [], shorts: [], ads: [], styles: {}, formats: {}, themes: {} });
  const [type, setType] = useState("artist");
  const [sourceId, setSourceId] = useState(null);
  const [style, setStyle] = useState("release");
  const [format, setFormat] = useState("tiktok");
  const [theme, setTheme] = useState("studio");
  const [customText, setCustomText] = useState("");
  const [customImage, setCustomImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [videoBusy, setVideoBusy] = useState(false);
  const [clipBusy, setClipBusy] = useState(false);
  const [clipSec, setClipSec] = useState(15);
  const [uploadFile, setUploadFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [extBusy, setExtBusy] = useState(false);
  const [packBusy, setPackBusy] = useState(false);
  const [packResult, setPackResult] = useState(null);

  useEffect(() => {
    api.get("/admin/viral-studio/sources")
      .then(({ data }) => setSources(data))
      .catch(() => toast.error("Accès refusé ou erreur sources"));
  }, []);

  // Reset source selection on type change
  useEffect(() => {
    setSourceId(null);
    setResult(null);
    setUploadFile(null);
    setExternalUrl("");
  }, [type]);

  const list = useMemo(() => {
    if (type === "artist") return sources.artists || [];
    if (type === "track")  return sources.tracks  || [];
    if (type === "short")  return sources.shorts  || [];
    if (type === "ad")     return sources.ads     || [];
    return [];
  }, [type, sources]);

  const handleGenerate = async () => {
    // upload : on bascule sur custom (texte libre + cover par défaut générique)
    if (type === "upload") {
      if (!uploadFile) { toast.error("Choisis une vidéo à téléverser"); return; }
      if (!customText.trim()) { toast.error("Saisis un titre / texte d'overlay"); return; }
    }
    if (type === "external" && !customText) { toast.error("Récupère d'abord les infos avec le bouton 🔍"); return; }
    if (["artist","track","short","ad"].includes(type) && !sourceId) {
      toast.error("Choisis une source"); return;
    }
    if (type === "custom" && !customText.trim()) { toast.error("Saisis un texte"); return; }

    // Mappe les types virtuels (upload/external) vers le backend type=custom
    const backendType = (type === "upload" || type === "external") ? "custom" : type;

    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/viral-studio/generate", {
        type: backendType, source_id: sourceId, style, format, theme,
        custom_text: customText, custom_image_url: customImage,
      });
      // Pour le mode upload, on injecte la vidéo locale comme source_video_url (blob URL)
      if (type === "upload" && uploadFile) {
        data.source_video_url = URL.createObjectURL(uploadFile);
        data.source_video_local = true;
      }
      setResult(data);
      toast.success("Visuel généré !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur génération");
    } finally {
      setLoading(false);
    }
  };

  // Récupère titre + thumbnail HD d'une URL YouTube/TikTok via oEmbed (légal)
  const fetchExternalMeta = async () => {
    if (!externalUrl.trim()) { toast.error("Colle une URL YouTube ou TikTok"); return; }
    setExtBusy(true);
    try {
      const { data } = await api.post("/admin/viral-studio/oembed", { url: externalUrl.trim() });
      setCustomText(data.title || data.author_name || "Vidéo");
      setCustomImage(data.thumbnail_url || "");
      toast.success(`Infos ${data.provider} récupérées`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec récupération");
    } finally {
      setExtBusy(false);
    }
  };

  const copy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1400);
      toast.success("Copié");
    } catch {
      toast.error("Copie impossible");
    }
  };

  const absUrl = (rel) => rel?.startsWith("http") ? rel : `${BACKEND}${rel}`;

  const downloadImage = async () => {
    if (!result?.image_url) return;
    const url = absUrl(result.image_url);
    try {
      const r = await fetch(url);
      const b = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = result.image_url.split("/").pop() || "viral.jpg";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(url, "_blank");
    }
  };

  // ── Pack complet : 12 visuels (4 thèmes × 3 formats) en ZIP ───
  const generatePack = async () => {
    if (["artist","track","short","ad"].includes(type) && !sourceId) { toast.error("Choisis une source"); return; }
    if (type === "custom" && !customText.trim()) { toast.error("Saisis un texte"); return; }
    if (type === "upload" || type === "external") { toast.error("Le pack complet n'est pas dispo pour upload/external"); return; }

    setPackBusy(true);
    setPackResult(null);
    try {
      const { data } = await api.post("/admin/viral-studio/generate-pack", {
        type, source_id: sourceId, style,
        custom_text: customText, custom_image_url: customImage,
      });
      setPackResult(data);
      toast.success(`Pack de ${data.count} visuels prêt !`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur génération pack");
    } finally {
      setPackBusy(false);
    }
  };

  const downloadPack = () => {
    if (!packResult?.zip_url) return;
    const a = document.createElement("a");
    a.href = absUrl(packResult.zip_url);
    a.download = packResult.zip_url.split("/").pop() || "voamada_pack.zip";
    a.click();
  };


  const fetchAsFile = async () => {
    const url = absUrl(result.image_url);
    const r = await fetch(url);
    const blob = await r.blob();
    const filename = result.image_url.split("/").pop() || "viral.jpg";
    return new File([blob], filename, { type: blob.type || "image/jpeg" });
  };

  const buildShareText = () =>
    `${result.captions?.[0] || ""}\n\n${(result.hashtags || []).join(" ")}`.trim();

  const nativeShare = async () => {
    if (!result) return;
    const text = buildShareText();
    try {
      const file = await fetchAsFile();
      const shareData = { files: [file], text, title: "Voamada" };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success("Partage ouvert");
        return;
      }
      if (navigator.share) {
        await navigator.share({ text, title: "Voamada" });
        toast.success("Partage ouvert");
        return;
      }
      // Fallback : copie caption + ouvre nouvel onglet image
      await navigator.clipboard.writeText(text);
      window.open(absUrl(result.image_url), "_blank");
      toast.info("Légende copiée — partage manuel ouvert");
    } catch (e) {
      if (e?.name === "AbortError") return; // user dismissed
      toast.error("Partage non supporté sur ce navigateur");
    }
  };

  const shareFacebook = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(buildShareText()); } catch (_) {}
    const u = encodeURIComponent(absUrl(result.image_url));
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    window.open(fbUrl, "_blank", "noopener,noreferrer,width=600,height=600");
    toast.success("Facebook ouvert (légende copiée)");
  };

  const openTikTok = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(buildShareText()); } catch (_) {}
    const isAndroid = /android/i.test(navigator.userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isMobile = isAndroid || isIOS;
    // 1) Télécharge l'image localement pour faciliter le post depuis l'app
    try {
      const file = await fetchAsFile();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(file);
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (_) {}
    // 2) Ouvre TikTok (app sur mobile, web sinon)
    let target;
    if (isMobile) {
      target = isAndroid ? "snssdk1233://" : "tiktok://";
    } else {
      target = "https://www.tiktok.com/upload";
    }
    setTimeout(() => { window.location.href = target; }, 350);
    toast.success("Image enregistrée + TikTok ouvert · légende copiée");
  };

  // ── Génération vidéo côté navigateur (Canvas + MediaRecorder) ────
  const exportVideo = async () => {
    if (!result?.image_url) return;
    setVideoBusy(true);
    try {
      const f = sources.formats?.[format];
      const W = f?.w || 1080;
      const H = f?.h || 1920;
      const url = absUrl(result.image_url);

      const img = await loadImage(url);

      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");

      // Choisit le mimetype supporté
      const mime = pickMime();
      if (!mime) { toast.error("Ton navigateur ne supporte pas l'export vidéo"); setVideoBusy(false); return; }

      const stream = canvas.captureStream(30);
      const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 5_000_000 });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      const stopped = new Promise((res) => { recorder.onstop = res; });
      recorder.start();

      // Animation Ken Burns : zoom de 1.0 → 1.12 sur 4 secondes
      const duration = 4000;
      const t0 = performance.now();
      const draw = (t) => {
        const elapsed = Math.min(duration, t - t0);
        const p = elapsed / duration;
        const scale = 1.0 + 0.12 * p;
        const w = W * scale;
        const h = H * scale;
        const dx = (W - w) / 2;
        const dy = (H - h) / 2;
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, W, H);
        ctx.drawImage(img, dx, dy, w, h);
        // Léger flash blanc d'intro (300 ms)
        if (elapsed < 300) {
          ctx.fillStyle = `rgba(255,255,255,${(1 - elapsed/300) * 0.35})`;
          ctx.fillRect(0, 0, W, H);
        }
        if (elapsed < duration) requestAnimationFrame(draw);
        else recorder.stop();
      };
      requestAnimationFrame(draw);

      await stopped;
      const blob = new Blob(chunks, { type: mime });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ext = mime.includes("mp4") ? "mp4" : "webm";
      a.download = `viral_${format}_${Date.now()}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Vidéo ${ext.toUpperCase()} exportée (4s, Ken-Burns)`);
    } catch (e) {
      console.error(e);
      toast.error("Échec export vidéo");
    } finally {
      setVideoBusy(false);
    }
  };

  // ── Clip promo : intro card 2s + vidéo (N s) + outro card 2s + audio ──
  const exportClipPromo = async () => {
    if (!result?.image_url || !result?.source_video_url) return;
    setClipBusy(true);
    try {
      const f = sources.formats?.[format];
      const W = f?.w || 1080;
      const H = f?.h || 1920;
      const overlayImg = await loadImage(absUrl(result.image_url));
      const videoUrl = result.source_video_local ? result.source_video_url : absUrl(result.source_video_url);

      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.src = videoUrl;
      video.preload = "auto";
      video.muted = false;
      video.playsInline = true;

      await new Promise((res, rej) => {
        video.onloadedmetadata = () => res();
        video.onerror = () => rej(new Error("Lecture vidéo impossible (CORS ou format non supporté)"));
      });

      const INTRO = 2, OUTRO = 2;
      const middleSec = Math.max(3, clipSec - INTRO - OUTRO);

      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");

      const canvasStream = canvas.captureStream(30);
      // Audio depuis la vidéo (si supporté)
      let audioTrack = null;
      try {
        const vs = (video.captureStream && video.captureStream()) || (video.mozCaptureStream && video.mozCaptureStream());
        audioTrack = vs?.getAudioTracks?.()[0] || null;
      } catch (_) {}
      const tracks = [canvasStream.getVideoTracks()[0]];
      if (audioTrack) tracks.push(audioTrack);
      const combined = new MediaStream(tracks);

      const mime = pickMime();
      if (!mime) { toast.error("Export vidéo non supporté sur ce navigateur"); setClipBusy(false); return; }
      const recorder = new MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 6_000_000 });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      const stopped = new Promise((res) => { recorder.onstop = res; });

      const drawCover = (src) => {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, W, H);
        const sW = src.videoWidth || src.width;
        const sH = src.videoHeight || src.height;
        if (!sW || !sH) return;
        const tR = W / H, srcR = sW / sH;
        let ow = sW, oh = sH, ox = 0, oy = 0;
        if (srcR > tR) { ow = sH * tR; ox = (sW - ow) / 2; }
        else            { oh = sW / tR; oy = (sH - oh) / 2; }
        ctx.drawImage(src, ox, oy, ow, oh, 0, 0, W, H);
      };
      const drawWatermark = () => {
        const fs = Math.max(20, Math.floor(W * 0.032));
        const pad = Math.floor(fs * 0.5);
        ctx.font = `bold ${fs}px Arial, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const txt = "voamada.com";
        const tw = ctx.measureText(txt).width + pad * 3;
        const bx = (W - tw) / 2;
        const by = H - fs * 2.6;
        ctx.fillStyle = "rgba(0,0,0,0.55)";
        ctx.fillRect(bx, by, tw, fs * 1.9);
        ctx.fillStyle = "#fff";
        ctx.fillText(txt, W / 2, by + fs);
      };

      recorder.start();

      // Phase 1 : intro (carte) avec fade-in 0.4s
      const t0 = performance.now();
      await new Promise((res) => {
        const tick = () => {
          const e = (performance.now() - t0) / 1000;
          drawCover(overlayImg);
          const a = Math.max(0, 1 - e / 0.4);
          if (a > 0) { ctx.fillStyle = `rgba(0,0,0,${a})`; ctx.fillRect(0, 0, W, H); }
          if (e < INTRO) requestAnimationFrame(tick); else res();
        };
        tick();
      });

      // Phase 2 : video playback
      video.currentTime = 0;
      try { await video.play(); } catch (_) {}
      const tm0 = performance.now();
      await new Promise((res) => {
        const tick = () => {
          const e = (performance.now() - tm0) / 1000;
          drawCover(video);
          drawWatermark();
          if (e < middleSec && !video.ended) requestAnimationFrame(tick);
          else { try { video.pause(); } catch (_) {} res(); }
        };
        tick();
      });

      // Phase 3 : outro
      const to0 = performance.now();
      await new Promise((res) => {
        const tick = () => {
          const e = (performance.now() - to0) / 1000;
          drawCover(overlayImg);
          if (e < OUTRO) requestAnimationFrame(tick); else res();
        };
        tick();
      });

      recorder.stop();
      await stopped;
      const blob = new Blob(chunks, { type: mime });
      const ext = mime.includes("mp4") ? "mp4" : "webm";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `clip_promo_${format}_${Date.now()}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Clip promo exporté (${clipSec}s, ${ext.toUpperCase()})`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Échec export clip promo");
    } finally {
      setClipBusy(false);
    }
  };


  return (
    <div data-testid="viral-studio-panel" className="space-y-6">
      <header className="rounded-2xl border border-white/10 bg-gradient-to-br from-fuchsia-600/15 via-orange-500/10 to-amber-500/10 p-5">
        <div className="flex items-center gap-3">
          <Sparkles className="text-amber-300" />
          <div>
            <h2 className="text-xl font-extrabold">Studio Viral — TikTok & Facebook</h2>
            <p className="text-sm text-white/70">
              Génère un visuel <strong>9:16 / 1:1 / 16:9</strong> + un pack de 5 légendes prêtes à coller. Tu peux aussi exporter en vidéo (4s, animation Ken-Burns) directement depuis ton navigateur.
            </p>
          </div>
        </div>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Colonne config */}
        <div className="lg:col-span-3 space-y-5">
          {/* Étape 1 : Type */}
          <Section title="1. Type de contenu">
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  data-testid={`vs-type-${t.key}`}
                  className={`px-3.5 py-2 rounded-full text-sm font-semibold border transition ${
                    type === t.key
                      ? "bg-amber-400/20 border-amber-400/60 text-amber-100"
                      : "bg-white/[0.03] border-white/10 text-white/70 hover:text-white"
                  }`}
                >
                  {t.emoji} {t.label}
                </button>
              ))}
            </div>
          </Section>

          {/* Étape 2 : Source */}
          {!["custom","upload","external"].includes(type) && (
            <Section title={`2. Choisir la source (${list.length})`}>
              {list.length === 0 ? (
                <p className="text-sm text-white/45 italic">Aucune source dispo pour ce type.</p>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-1 pr-1">
                  {list.map((s) => {
                    const title = s.title || s.name || `#${s.id}`;
                    const thumb = pickThumb(s);
                    const isSel = sourceId === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setSourceId(s.id)}
                        data-testid={`vs-source-${s.id}`}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl border transition ${
                          isSel
                            ? "bg-amber-400/15 border-amber-400/60"
                            : "bg-white/[0.02] border-white/10 hover:bg-white/[0.05]"
                        }`}
                      >
                        {thumb ? (
                          <img src={absUrl(thumb)} alt="" className="w-10 h-10 rounded-md object-cover bg-black/30" />
                        ) : (
                          <span className="w-10 h-10 rounded-md bg-white/5 flex items-center justify-center text-white/30">∅</span>
                        )}
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold truncate">{title}</span>
                          <span className="block text-[11px] text-white/40 truncate">
                            {s.artist_name || s.country || s.city || s.category || ""}
                          </span>
                        </span>
                        {isSel && <Check size={16} className="text-amber-300 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </Section>
          )}

          {type === "custom" && (
            <Section title="2. Texte libre + image (optionnel)">
              <textarea
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                rows={3}
                maxLength={120}
                placeholder="Ex: «On vient de drop un truc énorme»"
                data-testid="vs-custom-text"
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-amber-400/60"
              />
              <input
                type="text"
                value={customImage}
                onChange={(e) => setCustomImage(e.target.value)}
                placeholder="URL d'une image (optionnel)"
                data-testid="vs-custom-image"
                className="mt-2 w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-amber-400/60"
              />
            </Section>
          )}

          {type === "upload" && (
            <Section title="2. Téléverser ta vidéo + titre overlay">
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/*"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="vs-upload-input"
                className="block w-full text-sm text-white/70 file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-amber-400/15 file:text-amber-200 file:font-semibold hover:file:bg-amber-400/25"
              />
              {uploadFile && (
                <p className="mt-1.5 text-[11px] text-white/50">
                  📦 {uploadFile.name} · {(uploadFile.size / 1024 / 1024).toFixed(1)} Mo
                </p>
              )}
              <input
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                maxLength={80}
                placeholder="Titre / texte à afficher dans l'intro & outro"
                data-testid="vs-upload-title"
                className="mt-3 w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-amber-400/60"
              />
              <p className="mt-2 text-[11px] text-white/40 leading-snug">
                💡 La vidéo est traitée localement dans ton navigateur. Si tu as l'autorisation de l'artiste, tu peux uploader sa vidéo brute (rush) ou ton propre tournage.
              </p>
            </Section>
          )}

          {type === "external" && (
            <Section title="2. URL YouTube / TikTok (légal — utilise oEmbed)">
              <div className="flex gap-2">
                <input
                  type="url"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... ou https://www.tiktok.com/@.../video/..."
                  data-testid="vs-external-url"
                  className="flex-1 rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-amber-400/60"
                />
                <button
                  onClick={fetchExternalMeta}
                  disabled={extBusy}
                  data-testid="vs-external-fetch"
                  className="shrink-0 px-3 py-2 rounded-xl bg-amber-400/20 border border-amber-400/50 text-amber-100 text-sm font-bold hover:bg-amber-400/30 disabled:opacity-60"
                >
                  {extBusy ? <Loader2 size={14} className="animate-spin" /> : "🔍"}
                </button>
              </div>
              {customText && (
                <div className="mt-3 rounded-xl bg-white/[0.03] border border-white/10 p-3">
                  <div className="flex items-center gap-3">
                    {customImage && <img src={customImage} alt="" className="w-16 h-16 rounded-md object-cover bg-black/30" />}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{customText}</p>
                      <p className="text-[10px] text-white/40">Source : embed légal (pas de téléchargement). On génère un visuel promo qui pointe vers la vidéo originale.</p>
                    </div>
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Étape 3 : Style */}
          <Section title="3. Style visuel">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(sources.styles || {}).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setStyle(k)}
                  data-testid={`vs-style-${k}`}
                  className={`text-left px-3 py-2.5 rounded-xl border transition ${
                    style === k
                      ? "bg-amber-400/15 border-amber-400/60"
                      : "bg-white/[0.02] border-white/10 hover:bg-white/[0.05]"
                  }`}
                >
                  <div className="text-sm font-semibold">{v.label}</div>
                  <div className="text-[10px] text-white/40">{v.desc}</div>
                </button>
              ))}
            </div>
          </Section>

          {/* Étape 4 : Format */}
          <Section title="4. Format de sortie">
            <div className="flex flex-wrap gap-2">
              {Object.entries(sources.formats || {}).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setFormat(k)}
                  data-testid={`vs-format-${k}`}
                  className={`px-3.5 py-2 rounded-full text-sm font-semibold border transition ${
                    format === k
                      ? "bg-amber-400/20 border-amber-400/60 text-amber-100"
                      : "bg-white/[0.03] border-white/10 text-white/70 hover:text-white"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </Section>

          {/* Étape 5 : Thème visuel */}
          <Section title="5. Thème visuel">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(sources.themes || {}).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setTheme(k)}
                  data-testid={`vs-theme-${k}`}
                  className={`text-left px-3 py-2.5 rounded-xl border transition ${
                    theme === k
                      ? "bg-amber-400/15 border-amber-400/60"
                      : "bg-white/[0.02] border-white/10 hover:bg-white/[0.05]"
                  }`}
                >
                  <div className="text-sm font-semibold">{v.label}</div>
                  <div className="text-[10px] text-white/40">{v.desc}</div>
                </button>
              ))}
            </div>
          </Section>

          <button
            onClick={handleGenerate}
            disabled={loading}
            data-testid="vs-generate-btn"
            className="w-full py-3.5 rounded-2xl font-bold text-black bg-gradient-to-r from-amber-300 via-orange-400 to-pink-400 hover:brightness-105 transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Génération…</> : <><Sparkles size={18} /> Générer le visuel</>}
          </button>

          {/* Pack complet — uniquement pour les sources serveur */}
          {["artist","track","short","ad","custom"].includes(type) && (
            <button
              onClick={generatePack}
              disabled={packBusy}
              data-testid="vs-generate-pack-btn"
              className="w-full py-3 rounded-2xl font-bold border-2 border-dashed border-fuchsia-400/50 text-fuchsia-200 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {packBusy
                ? <><Loader2 size={18} className="animate-spin" /> Génération du pack (12 visuels)…</>
                : <>📦 Pack complet · 4 thèmes × 3 formats = 12 visuels (ZIP)</>}
            </button>
          )}

          {packResult && (
            <div data-testid="vs-pack-result" className="rounded-2xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-3 space-y-2">
              <p className="text-sm font-semibold text-fuchsia-100">
                ✅ Pack de {packResult.count} visuels prêt
              </p>
              <button
                onClick={downloadPack}
                data-testid="vs-pack-download"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-fuchsia-500 text-white text-sm font-bold hover:bg-fuchsia-500/90"
              >
                <Download size={14} /> Télécharger le ZIP
              </button>
              <p className="text-[10px] text-white/50">
                Le ZIP contient les 12 fichiers JPG nommés `studio_tiktok.jpg`, `vinyl_fb_square.jpg`, etc. + un README.txt.
              </p>
            </div>
          )}
        </div>

        {/* Colonne aperçu / résultat */}
        <div className="lg:col-span-2 space-y-4">
          <Section title="Aperçu">
            {!result ? (
              <div className="aspect-[9/16] rounded-2xl border border-dashed border-white/15 bg-white/[0.02] flex items-center justify-center text-white/40 text-sm text-center px-4">
                Ton visuel apparaîtra ici après génération.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
                  <img
                    src={absUrl(result.image_url)}
                    alt="visuel viral"
                    data-testid="vs-result-image"
                    className="block w-full h-auto"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={downloadImage}
                    data-testid="vs-download-img"
                    className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white text-black text-sm font-bold hover:bg-white/90"
                  >
                    <ImageIcon size={14} /> Télécharger image
                  </button>
                  <button
                    onClick={exportVideo}
                    disabled={videoBusy}
                    data-testid="vs-export-video"
                    className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-fuchsia-500 text-white text-sm font-bold hover:bg-fuchsia-500/90 disabled:opacity-60"
                  >
                    {videoBusy ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
                    Exporter vidéo (4s)
                  </button>
                </div>

                {/* Clip promo intro+vidéo+outro avec audio — affiché si vidéo source dispo */}
                {result.source_video_url && (
                  <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-pink-500/10 p-3 space-y-2">
                    <p className="text-[10px] uppercase tracking-wider text-amber-200 font-semibold">
                      🎬 Clip promo (intro + vidéo + outro + audio)
                    </p>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-white/70">Durée</label>
                      <select
                        value={clipSec}
                        onChange={(e) => setClipSec(Number(e.target.value))}
                        data-testid="vs-clip-duration"
                        className="rounded-lg bg-black/40 border border-white/15 px-2 py-1.5 text-xs text-white focus:outline-none"
                      >
                        <option value={15}>15s (TikTok)</option>
                        <option value={30}>30s (Reels)</option>
                        <option value={60}>60s</option>
                      </select>
                      <button
                        onClick={exportClipPromo}
                        disabled={clipBusy}
                        data-testid="vs-export-clip"
                        className="ml-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-amber-400 text-black text-xs font-bold hover:brightness-105 disabled:opacity-60"
                      >
                        {clipBusy ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
                        Exporter clip
                      </button>
                    </div>
                    <p className="text-[10px] text-white/50 leading-snug">
                      2s intro (carte brand) + {Math.max(3, clipSec - 4)}s vidéo + 2s outro. Audio de la vidéo conservé.
                    </p>
                  </div>
                )}

                {/* Partage rapide : Web Share / Facebook / TikTok */}
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                  <p className="text-[10px] uppercase tracking-wider text-white/45 font-semibold mb-2">
                    Partage rapide · légende auto-copiée
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={nativeShare}
                      data-testid="vs-share-native"
                      title="Partager via le menu natif du téléphone (TikTok, WhatsApp, Insta, etc.)"
                      className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 text-white text-xs font-bold hover:brightness-110"
                    >
                      <Share2 size={14} /> Partager
                    </button>
                    <button
                      onClick={shareFacebook}
                      data-testid="vs-share-fb"
                      title="Ouvre la fenêtre de partage Facebook"
                      className="inline-flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl text-white text-xs font-bold hover:brightness-110"
                      style={{ background: "#1877F2" }}
                    >
                      <Facebook size={14} /> Facebook
                    </button>
                    <button
                      onClick={openTikTok}
                      data-testid="vs-share-tiktok"
                      title="Télécharge l'image et ouvre TikTok (app sur mobile, upload web sinon)"
                      className="relative inline-flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl bg-black text-white text-xs font-bold ring-1 ring-white/10 overflow-hidden hover:brightness-110"
                    >
                      <span className="absolute -left-0.5 top-0 bottom-0 w-1 bg-[#25F4EE]" />
                      <span className="absolute -right-0.5 top-0 bottom-0 w-1 bg-[#FE2C55]" />
                      <TikTokIconInline /> TikTok
                    </button>
                  </div>
                  <p className="text-[10px] text-white/40 mt-2 leading-snug">
                    Sur mobile : "Partager" ouvre directement TikTok/Insta/WhatsApp avec l'image et la légende attachées (Web Share API).
                  </p>
                </div>
              </div>
            )}
          </Section>

          {result?.captions?.length > 0 && (
            <Section title="5 légendes prêtes">
              <div className="space-y-2">
                {result.captions.map((c, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <pre className="whitespace-pre-wrap text-xs text-white/80 font-sans leading-relaxed">{c}</pre>
                    <button
                      onClick={() => copy(c, i)}
                      data-testid={`vs-copy-caption-${i}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-300 hover:text-amber-200"
                    >
                      {copiedIdx === i ? <><Check size={12} /> Copié</> : <><Copy size={12} /> Copier</>}
                    </button>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {result?.hashtags?.length > 0 && (
            <Section title={<span className="inline-flex items-center gap-1"><Hash size={13} /> Hashtags</span>}>
              <p className="text-xs text-white/70 leading-relaxed">{result.hashtags.join(" ")}</p>
              <button
                onClick={() => copy(result.hashtags.join(" "), -1)}
                data-testid="vs-copy-hashtags"
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-300 hover:text-amber-200"
              >
                <Copy size={12} /> Copier les hashtags
              </button>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
    <h3 className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-3">{title}</h3>
    {children}
  </div>
);

const TikTokIconInline = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.32a8.16 8.16 0 0 0 4.77 1.52V6.4a4.85 4.85 0 0 1-1.84-.04Z"/>
  </svg>
);

const pickThumb = (s) => s.picture || s.cover_path || (s.photos?.[0]) || null;

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

const pickMime = () => {
  const candidates = [
    "video/mp4;codecs=avc1",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
};

export default AdminViralStudioPanel;
